import { API_BASE, apiAuthHeaders } from './api-client';

// ---- Server health LEDs (API + Worker) ----
export interface HealthLeds {
  el: HTMLElement;
  setApi: (ok: boolean) => void;
  setWorker: (ok: boolean) => void;
}

function makeLed(label: string, idPrefix: string): { row: HTMLElement; dot: HTMLElement; text: HTMLElement } {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:6px;font-size:11px;color:var(--text-muted);margin:2px 0;';

  const dot = document.createElement('span');
  dot.id = `${idPrefix}-dot`;
  // Solid bright dot + 8px glow shadow → stands out on warm white background.
  dot.style.cssText = 'display:inline-block;width:12px;height:12px;border-radius:50%;background:#666;box-shadow:0 0 4px rgba(0,0,0,0.5);cursor:pointer;transition:background 0.2s,box-shadow 0.2s;border:2px solid rgba(0,0,0,0.1);';

  const text = document.createElement('span');
  text.id = `${idPrefix}-text`;
  text.textContent = label;
  text.style.cursor = 'pointer';

  row.appendChild(dot);
  row.appendChild(text);
  return { row, dot, text };
}

export function createHealthLeds(): HealthLeds {
  const el = document.createElement('div');
  el.id = 'health-leds';
  el.style.cssText = 'display:flex;gap:12px;padding:8px 12px;background:var(--bg-surface);border:1px solid var(--border);border-radius:4px;pointer-events:auto;';

  const api = makeLed('API', 'health-api');
  const worker = makeLed('Worker', 'health-worker');

  const setApi = (ok: boolean) => {
    api.dot.style.background = ok ? 'var(--success)' : 'var(--error)';
    api.dot.style.boxShadow = ok ? '0 0 6px var(--success)' : '0 0 6px var(--error)';
  };
  const setWorker = (ok: boolean) => {
    worker.dot.style.background = ok ? 'var(--success)' : 'var(--error)';
    worker.dot.style.boxShadow = ok ? '0 0 6px var(--success)' : '0 0 6px var(--error)';
  };

  el.appendChild(api.row);
  el.appendChild(worker.row);
  return { el, setApi, setWorker };
}

// ---- Model health LEDs (Watertight + Support) ----
// 2 LEDs in sidebar; click LED → toggle highlight (red = open edge, orange = face >45°).
// LED green = OK (watertight / no support needed). LED red = fail (edge/face count).
export interface ModelHealthLeds {
  el: HTMLElement;
  setWatertight: (ok: boolean, edgeCount: number) => void;
  setSupport: (ok: boolean, overhangCount: number) => void;
  setWatertightUnknown: () => void;
  setSupportUnknown: () => void;
  setOnWatertightClick: (cb: () => void) => void;
  setOnSupportClick: (cb: () => void) => void;
  // R19: let main.ts check state on click → silent if OK, toggle if fail
  isWatertightOk: () => boolean;
  isSupportOk: () => boolean;
}
export function createModelHealthLeds(): ModelHealthLeds {
  const el = document.createElement('div');
  el.id = 'model-health-leds';
  // TASK 20 (2026-09-30): pointer-events:auto is REQUIRED, not cosmetic.
  // The container this widget goes into (left-overlay-col) sets `pointer-events:none` so it
  // does not swallow drags meant for the 3D canvas. Children inherit that unless they opt
  // back in, and makeLed() does not set it - so a bare appendChild would make the LEDs
  // VISIBLE but not clickable, leaving both toggle handlers dead. Verified by tracing the
  // inheritance: none -> row(undefined) -> dot(undefined) resolves to `none`.
  // align-self:stretch so this row matches the width of the widest panel in left-overlay-col
  // rather than shrinking to its own two LEDs.
  el.style.cssText = 'align-self:stretch;box-sizing:border-box;display:flex;gap:14px;padding:8px 10px;background:var(--bg);border:1px solid var(--border);border-radius:4px;margin-bottom:12px;pointer-events:auto;';

  const wt = makeLed('Watertight', 'model-wt');
  const sup = makeLed('Support', 'model-sup');
  wt.row.title = 'Click → toggle non-watertight edges (red)';
  sup.row.title = 'Click → toggle support-required areas >45° (orange)';

  let onWtClick: () => void = () => {};
  let onSupClick: () => void = () => {};
  wt.row.addEventListener('click', () => onWtClick());
  sup.row.addEventListener('click', () => onSupClick());

  let wtOk = false;
  let supOk = false;

  // Sinnerer glow for LEDs on white background — 8px glow + green-600/red-600.
  const setColor = (dot: HTMLElement, ok: boolean) => {
    dot.style.background = ok ? '#16a34a' : '#dc2626';
    dot.style.boxShadow = ok
      ? '0 0 8px #16a34a, 0 0 2px #16a34a'
      : '0 0 8px #dc2626, 0 0 2px #dc2626';
    dot.style.borderColor = ok ? '#15803d' : '#b91c1c';
  };
  const setWatertight = (ok: boolean, edgeCount: number) => {
    wtOk = ok;
    setColor(wt.dot, ok);
    wt.text.textContent = ok
      ? 'Watertight ✓'
      : `Open edges: ${edgeCount}`;
  };
  const setWatertightUnknown = () => {
    wtOk = false;
    wt.dot.style.background = '#9ca3af';
    wt.dot.style.boxShadow = '0 0 6px #9ca3af';
    wt.dot.style.borderColor = '#6b7280';
    wt.text.textContent = 'Not checked';
  };
  const setSupport = (ok: boolean, overhangCount: number) => {
    supOk = ok;
    setColor(sup.dot, ok);
    sup.text.textContent = ok
      ? 'No support needed'
      : `Overhang faces: ${overhangCount}`;
  };
  const setSupportUnknown = () => {
    supOk = false;
    sup.dot.style.background = '#9ca3af';
    sup.dot.style.boxShadow = '0 0 6px #9ca3af';
    sup.dot.style.borderColor = '#6b7280';
    sup.text.textContent = 'Not checked';
  };

  el.appendChild(wt.row);
  el.appendChild(sup.row);
  setWatertightUnknown();
  setSupportUnknown();
  return {
    el,
    setWatertight,
    setSupport,
    setWatertightUnknown,
    setSupportUnknown,
    setOnWatertightClick: (cb) => { onWtClick = cb; },
    setOnSupportClick: (cb) => { onSupClick = cb; },
    isWatertightOk: () => wtOk,
    isSupportOk: () => supOk,
  };
}

// API Base URL config: uses VITE_API_URL env var if set, otherwise points to local API server on port 38592 (unless on prod domain *.boxed3d.com)
// API configuration and preview requests live in api-client.ts.

// T1.6: temporary API lock with ONE shared key. Web sends key in header
// Authorization. Key comes from VITE_API_KEY at build time (file .env.local, not committed).
// WARNING: key is inside the bundle and visible in DevTools — this is only a gate,
// not real security. See DOC_/API_KEY.md before enabling for public users.

/** Headers for all API requests, including key if the web build was built with a key. */
// ---- Health poll: ping /api/health every intervalMs ----
export function startHealthPoll(leds: HealthLeds, intervalMs = 5000): () => void {
  let lastApiOk = false;
  let lastWorkerOk = false;
  const poll = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/health`, { cache: 'no-store', headers: apiAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const apiOk = data.status === 'ok';
      const workerOk = !!data.workerConfigured;
      if (apiOk !== lastApiOk) { leds.setApi(apiOk); lastApiOk = apiOk; }
      if (workerOk !== lastWorkerOk) { leds.setWorker(workerOk); lastWorkerOk = workerOk; }
    } catch {
      if (lastApiOk) { leds.setApi(false); lastApiOk = false; }
      if (lastWorkerOk) { leds.setWorker(false); lastWorkerOk = false; }
    }
  };
  poll();
  const id = setInterval(poll, intervalMs);
  return () => clearInterval(id);
}


